import type { Locale } from 'date-fns'
import { setDefaultOptions } from 'date-fns'
import { ca, de, enGB, es } from 'date-fns/locale'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { index } from '@last/core'

import { getConfig } from '@/api/config'
import i18n from '@/i18n'
import { useAuthStore } from '@/store/auth'
import type { Config, Language, Printer } from '@/types/config'
import type { DeviceConfig } from '@/types/device'

export const useConfigStore = defineStore(
  'config',
  () => {
    // @ts-expect-error Minimal default when offline
    const config = ref<Config>({
      employees: {
        selectionType: 'names' as 'names' | 'pin'
      },
      virtualBrands: [],
      locationCountryCode: 'ES',
      currencyCode: 'EUR',
      paymentMethods: [],
      blindTill: false,
      billCopies: 1,
      locationName: '',
      enableKitchenOrders: true,
      printers: [],
      autoprintReceipts: false,
      autoprintKitchenOrders: false,
      autoprintBills: false,
      dataphoneConfigs: [],
      adyenDataphones: [],
      preparationMinutes: 0,
      showTakeAwayTabPopup: false,
      onlyMasterPrinting: false,
      beepers: []
    })

    const mute = ref<boolean>(false)

    const fontSize = ref<number>(16)

    const darkMode = ref<boolean>(true)

    const selectedPrinterId = ref<string>('all')

    const screenConfig = ref<{
      mode: 'horizontal' | 'masonry' | 'expo' | 'allday'
      filter: 'open' | 'closed'
      showSummary: boolean
      thresholds: { warning: number; critical: number }
    }>({
      mode: 'horizontal',
      filter: 'open',
      showSummary: false,
      thresholds: {
        warning: 5,
        critical: 10
      }
    })

    const device = ref<DeviceConfig>({
      id: 'kds',
      mode: 'master',
      platform: 'web',
      accessToken: '',
      isDataphone: false
    })

    const printers = computed(() => {
      return index(
        config.value.printers.filter(p => p.type === 'KDS'),
        'id'
      ) as Record<string, Printer>
    })

    const appVersion = computed(() => {
      return device.value.appVersion || 'web'
    })

    async function refreshConfig(inputConfig?: Config) {
      const auth = useAuthStore()
      if (!auth.isAuthenticated) return
      let newConfig = inputConfig
      if (!newConfig) {
        try {
          newConfig = await getConfig()
        } catch {
          newConfig = {
            ...config.value
          }
        }
      }

      const language = newConfig.language || 'es'
      i18n.global.locale.value = language
      const locales: Record<Language, Locale> = {
        es: es,
        ca: ca,
        de: de,
        en: enGB
      }
      setDefaultOptions({
        locale: locales[language]
      })
      config.value = newConfig
    }

    watch(
      fontSize,
      value => {
        document.documentElement.style.setProperty('font-size', `${value}px`)
      },
      { immediate: true }
    )

    watch(
      darkMode,
      value => {
        if (value) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      },
      { immediate: true }
    )

    return {
      config,
      mute,
      fontSize,
      darkMode,
      device,
      appVersion,
      printers,
      selectedPrinterId,
      screenConfig,
      refreshConfig
    }
  },
  {
    persist: true
  }
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useConfigStore, import.meta.hot))
}
